import { formatEther, formatGwei, parseEther } from "viem";

/**
 * Gas figures shared by the deploy flow's wallet card, the postchecks and the top-up script. A settle with the
 * hybrid path (two transfers and a mint) stays under SETTLE_GAS; the figures are counts of settles, so they hold on
 * any chain once multiplied by its gas price.
 */
export const SETTLE_GAS = 150_000n;
/** The settles a new submitter's funding pays (deploy.html step 6). */
export const FUNDING_SETTLES = 100n;
/** Below this many settles a submitter is low on gas: the postchecks warn, the top-up script sends. */
export const LOW_GAS_SETTLES = 10n;
/** 0.0001 ETH, the page's figure on Base (0.006 gwei): the funding never goes below it. */
export const MIN_FUNDING_WEI = 100_000_000_000_000n;
const EIGHT_DECIMALS = 10_000_000_000n;

/** The wei of `settles` settles at a gas price. */
export function settlesWei(settles: bigint, gasPrice: bigint): bigint {
  return settles * SETTLE_GAS * gasPrice;
}

/** The ETH the contracts card sends a new submitter: FUNDING_SETTLES settles at the gas price, at least the minimum, in whole 8-decimal steps. */
export function submitterFunding(gasPrice: bigint): bigint {
  const wei = settlesWei(FUNDING_SETTLES, gasPrice);
  const floored = wei > MIN_FUNDING_WEI ? wei : MIN_FUNDING_WEI;
  return ((floored + EIGHT_DECIMALS - 1n) / EIGHT_DECIMALS) * EIGHT_DECIMALS;
}

/** How many settles a balance pays at a gas price; null when gas is free (a gas price of 0). */
export function settlesLeft(balance: bigint, gasPrice: bigint): number | null {
  if (gasPrice <= 0n) return null;
  return Number(balance / (SETTLE_GAS * gasPrice));
}

/** Whether a submitter is low on gas: fewer than LOW_GAS_SETTLES settles at the current gas price. */
export function lowOnGas(balance: bigint, gasPrice: bigint): boolean {
  const left = settlesLeft(balance, gasPrice);
  return left !== null && left < Number(LOW_GAS_SETTLES);
}

/** "0.0001" from wei: up to 8 decimals, trailing zeros removed. */
export function formatEth(wei: bigint): string {
  const [whole, fraction = ""] = formatEther(wei).split(".");
  const f = fraction.slice(0, 8).replace(/0+$/, "");
  return f ? `${whole}.${f}` : whole!;
}

/** "0.006" from a gas price in wei. */
export function formatGasPrice(wei: bigint): string {
  const [whole, fraction = ""] = formatGwei(wei).split(".");
  const f = fraction.slice(0, 6).replace(/0+$/, "");
  return f ? `${whole}.${f}` : whole!;
}

/** Wei from a decimal ETH text ("0.0001"); throws on a text that is not a number. */
export function parseEth(text: string): bigint {
  return parseEther(text.trim());
}

/** "0.000008 ETH ≈ 8 settles at 0.006 gwei · low on gas": the submitter's gas as the postchecks and the Console print it. */
export function gasText(balance: bigint, gasPrice: bigint): string {
  const left = settlesLeft(balance, gasPrice);
  const settles = left === null ? "gas is free" : `≈ ${left} settles at ${formatGasPrice(gasPrice)} gwei`;
  return `${formatEth(balance)} ETH · ${settles}${lowOnGas(balance, gasPrice) ? " · low on gas" : ""}`;
}
